'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { authAPI, systemAPI } from '../services/api';

const Login = ({ setUser }) => {
  const [loginMethod, setLoginMethod] = useState('email'); // 'phone' or 'email'
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: '',
    otp: ''
  });
  const [step, setStep] = useState('login'); // 'login' or 'otp'
  const [otpId, setOtpId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [systemSettings, setSystemSettings] = useState(null);

  useEffect(() => {
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const settings = await systemAPI.getSystemSettings();
      setSystemSettings(settings);
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  const siteName = systemSettings?.siteName || 'Bus Ticket System';
  const router = useRouter();

  const { phone, email, password, otp } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle phone number and password login
  const onPhoneLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // First verify phone and password
      // In a real implementation, you might want to verify password first
      // For now, we'll directly request OTP after validating phone number
      
      if (!phone) {
        throw new Error('Please enter your phone number');
      }

      // Request OTP
      const otpResponse = await authAPI.requestOTP(phone);
      setOtpId(otpResponse.otpId);
      setStep('otp');
      setSuccess('OTP sent to your phone number');
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Handle email and password login
  const onEmailLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Make direct fetch call to get proper error message from backend
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000/api'}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const userData = await response.json();

      if (!response.ok) {
        throw new Error(userData.message || 'Login failed');
      }
      
      // Save token to localStorage
      localStorage.setItem('token', userData.token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Log user data for debugging
      console.log('Email login successful. User data:', userData);
      console.log('User role:', userData.role);
      
      // Show success toast
      if (typeof window !== 'undefined' && window.addToast) {
        window.addToast('Login successful! Welcome back.', 'success');
      }
      
      // Call parent function to update app state
      if (setUser) {
        setUser(userData);
      }
      
      // Dispatch custom event to notify other components of auth change
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('authChange'));
      }
      
      // Redirect based on user role
      switch (userData.role) {
        case 'admin':
          router.push('/admin/dashboard');
          break;
        case 'provider':
          router.push('/provider/dashboard');
          break;
        case 'counter_manager':
          router.push('/counter/dashboard');
          break;
        case 'support':
          router.push('/support/dashboard');
          break;
        case 'developer':
          router.push('/developer/dashboard');
          break;
        case 'customer_care':
          router.push('/customer-care/dashboard');
          break;
        default:
          router.push('/search');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      // Also show alert for immediate feedback
      // alert(errorMessage);
      // Show error toast
      if (typeof window !== 'undefined' && window.addToast) {
        window.addToast(errorMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification
  const onOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.verifyOTP(phone, otp);
      
      // Extract user data from response
      const userData = response.user || response;
      const token = response.token || userData.token;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Show success toast
      if (typeof window !== 'undefined' && window.addToast) {
        window.addToast('Login successful! Welcome back.', 'success');
      }
      
      // Call parent function to update app state
      if (setUser) {
        setUser(userData);
      }
      
      // Dispatch custom event to notify other components of auth change
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('authChange'));
      }
      
      // Log user data for debugging
      console.log('Login successful. User data:', userData);
      console.log('User role:', userData.role);
      
      // Redirect based on user role
      switch (userData.role) {
        case 'admin':
          console.log('Redirecting admin to admin dashboard');
          router.push('/admin/dashboard');
          break;
        case 'provider':
          console.log('Redirecting provider to provider dashboard');
          router.push('/provider/dashboard');
          break;
        case 'counter_manager':
          console.log('Redirecting counter manager to counter dashboard');
          router.push('/counter/dashboard');
          break;
        case 'support':
          console.log('Redirecting support to support tickets');
          router.push('/support/tickets');
          break;
        default:
          console.log('Redirecting to search page');
          router.push('/search');
      }
    } catch (err) {
      setError(err.message || 'Invalid OTP');
      // Show error toast
      if (typeof window !== 'undefined' && window.addToast) {
        window.addToast(err.message || 'Invalid OTP', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP resend
  const resendOtp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const otpResponse = await authAPI.requestOTP(phone);
      setOtpId(otpResponse.otpId);
      setSuccess('OTP resent successfully');
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Back to login form
  const backToLogin = () => {
    setStep('login');
    setFormData({ ...formData, otp: '' });
  };

  const handleGoogleLogin = async () => {
    try {
      // Redirect to Google OAuth endpoint for login
      await authAPI.googleLogin();
    } catch (err) {
      setError(err.message || 'Google login failed');
      // Show error toast
      if (typeof window !== 'undefined' && window.addToast) {
        window.addToast(err.message || 'Google login failed', 'error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-2xl shadow-2xl">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-[var(--primary)] flex items-center justify-center mb-4">
            <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">
            Welcome Back
          </h2>
          <p className="mt-2 text-gray-600">
            Sign in to your {siteName} account
          </p>
        </div>
        
        <div className="flex border-b">
          <button
            type="button"
            className={`py-2 px-4 text-sm font-medium ${loginMethod === 'phone' ? 'border-b-2 border-[#007f80] text-[#007f80]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setLoginMethod('phone')}
          >
            Phone Login
          </button>
          <button
            type="button"
            className={`py-2 px-4 text-sm font-medium ${loginMethod === 'email' ? 'border-b-2 border-[#007f80] text-[#007f80]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setLoginMethod('email')}
          >
            Email Login
          </button>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}
        
        {loginMethod === 'phone' ? (
          step === 'login' ? (
            <form className="mt-8 space-y-6" onSubmit={onPhoneLoginSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] sm:text-sm"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#007f80] hover:bg-[#006667] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007f80] transition duration-300 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={onOtpSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">OTP</label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    required
                    className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] sm:text-sm"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={onChange}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    We've sent a 6-digit code to {phone}
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative flex-1 flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#007f80] hover:bg-[#006667] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007f80] transition duration-300 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </button>
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={loading}
                  className="group relative flex-1 flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007f80] transition duration-300"
                >
                  Resend
                </button>
              </div>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={backToLogin}
                  className="text-sm font-medium text-[#007f80] hover:text-[#006667]"
                >
                  Change phone number
                </button>
              </div>
            </form>
          )
        ) : (
          <form className="mt-8 space-y-6" onSubmit={onEmailLoginSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] sm:text-sm"
                  placeholder="Enter your email"
                  value={email}
                  onChange={onChange}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)] sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary)] border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link href="/auth/forgot-password" className="font-medium text-[#007f80] hover:text-[#006667]">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#007f80] hover:bg-[#006667] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007f80] transition duration-300 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        )}
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with
              </span>
            </div>
          </div>

          <div className="mt-6">
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={handleGoogleLogin}
                className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary)]"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link href="/register" className="font-medium text-[#007f80] hover:text-[#006667]">
              Register here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;